import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UserService } from '../../../auth/user.service';
import {
	AlarisComplexTableComponent,
	AlarisDialogService,
	AlarisFilterManagerComponent,
	AlarisTableStateService,
	EditPanelWidth,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR
} from '@campaign-portal/components-library';
import { TranslationRule } from '@helpers/types/models';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {FilterType, Paging} from '@campaign-portal/namespace/common/rpc.params';
import { TranslationRulesFieldsService } from './translation-rules-fields.service';
import { TranslationRulesService } from './translation-rules.service';
import { EditTranslationRulesComponent } from './edit-rule/edit-rule.component';
import { TranslationRulesDialogComponent } from './dialog/dialog.component';
import {SystemCodesService} from "../system-codes/system-codes.service";

@Component({
	selector: 'app-translation-rules',
	templateUrl: './translation-rules.component.html',
	styleUrls: [
		// eslint-disable-next-line max-len
		'../../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
		'./translation-rules.component.scss'
	],
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService }
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TranslationRulesComponent extends AlarisComplexTableComponent<TranslationRule> {
	override readonly rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-edit',
			label: 'actions.edit',
			action: this.edit.bind(this),
			disabled: (): boolean => {
				return !this.user.allowed(['translation_rules.edit']);
			}
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['translation_rules.edit']);
			}
		}
	];
	override readonly dblClickRowAction = this.edit;
	override readonly pageSizeOptions = [25, 50, 100, 200];
	override readonly paging: Paging = {
		Skip: this.activePage,
		Take: this.pageSize
	};

	constructor(
		public readonly user: UserService,
		dataService: TranslationRulesService,
		public override readonly fieldsService: TranslationRulesFieldsService,
		tableSettingsService: TableSettingsService,
		private readonly dialog: AlarisDialogService,
		public readonly service: TranslationRulesService,
		public readonly codesService: SystemCodesService
	) {
		super(dataService, fieldsService, tableSettingsService, TableTypes.INTERNAL);
		this.state.filters()
			.set(this.fieldsService.id.variable, { enabled: false, filterType: FilterType.LIKE })
			.set(this.fieldsService.name.variable, { enabled: true, filterType: FilterType.LIKE })
			.set(this.fieldsService.sourceType.variable, { enabled: true, filterType: FilterType.LIKE })
			.set(this.fieldsService.responseCode.variable, { enabled: true, filterType: FilterType.LIKE })
			.set(this.fieldsService.systemCode.variable, { enabled: true, filterType: FilterType.LIKE })
			.set(this.fieldsService.status.variable, { enabled: true, filterType: FilterType.LIKE })
			.set(this.fieldsService.lastUpdated.variable, { enabled: false, filterType: FilterType.LIKE })
		this.codesService.read().pipe(takeUntilDestroyed()).subscribe();
	}

	edit(rule?: TranslationRule): void {
		if (this.user.allowed(['translation_rules.edit'])) {
			this.editPanel.open<EditTranslationRulesComponent,
					{ rule: TranslationRule }, boolean>(EditTranslationRulesComponent, EditPanelWidth.SM, {rule})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((result) => {
					if (result) {
						this.load();
					}
				});
		}
	}

	delete(rule: TranslationRule): void {
		this.dialog
			.open<boolean>(TranslationRulesDialogComponent, { autoFocus: false, data: { rule } })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.load();
				}
			});
	}

	override editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.MD, {
			filtersChange: this.filtersChange,
			filters: this.state.filters,
			tHeaders: this.tHeaders()
		});
	}

	formatDate(inputDate: string | undefined | null): string {
		if (inputDate) {
			const date = new Date(inputDate);
			const pad = (num: number): string => num.toString().padStart(2, '0');

			const day = pad(date.getDate());
			const month = pad(date.getMonth() + 1);
			const year = date.getFullYear();

			const hours = pad(date.getHours());
			const minutes = pad(date.getMinutes());
			const seconds = pad(date.getSeconds());

			return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
		} else {
			return '';
		}
	}
}
